import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useInView } from 'react-intersection-observer';
import Typography from '../../../../components/Typography';
import Button from '../../../../components/Button';
import { MuiTextField } from '../../../../components/MuiComponents/FormInputs';
import SearchIcon from '../../../../components/SvgIcons/SearchIcon';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import {
  getAllDataFromPages,
  platformMapping,
  showPlatformIcon,
} from '../../../../actions/commonActions';
import { useAssetManagement } from '../../../../hooks/dashboard';
import { setAddNewAssetsModal } from '../../../../redux/reducers/dashboard';
import Storage from '../../../../utils/Storage';
import {
  SIEMBA_ADMIN,
  SIEMBA_CUSTOMER,
  SIEMBA_CUSTOMER_ENGINEER,
  SIEMBA_TESTER,
} from '../../../../utils/roles';
import { PermissionProvider } from '../../../../hoc/withPermissions';
import {
  API,
  IT_INFRASTRUCTURE,
  MOBILE_APP,
  WEB_APP,
} from '../../../../utils/constants/assetTypeRegistry';
import { CUSTOMER_LEADER } from '../../../../utils/constants/customerRoles';

/** ROAR-4376: customer Pentest Manager / POC (`asset_poc`) must not see inventory “Create New Asset” entry points. */
const GENPT_SCHEDULE_CREATE_ASSET_ROLES = [
  SIEMBA_ADMIN,
  SIEMBA_TESTER,
  SIEMBA_CUSTOMER,
  CUSTOMER_LEADER
];

const TARGET_ASSET_ICON_SRC = '/images/genpt/target_asset.svg';

/** GenPT / forms expect a full URL with protocol */
function normalizeTargetUrl(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const t = raw.trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

/**
 * Supports flat `assetManagement/listAssets` rows (`id`, `name`, `url`, …) and
 * nested All Assets v2 rows (`asset_id`, `asset: { target, name, … }`).
 */
function mapRowToPickedAsset(row) {
  const nested = row?.asset;
  let assetId;
  let name;
  let rawUrl;
  let platform_category;
  let exposure;
  let statusLabel;

  if (nested) {
    assetId = row.asset_id ?? nested.id;
    name = (nested.name ?? '').trim();
    rawUrl = (nested.target ?? nested.url ?? '').trim();
    platform_category = nested.platform_category ?? WEB_APP;
    exposure = row.exposure ?? nested.mode ?? null;
    statusLabel = nested.status ?? row.status;
  } else {
    assetId = row.id;
    name = (row.name ?? '').trim();
    rawUrl = (row.url ?? '').trim();
    platform_category = row.platform_category ?? WEB_APP;
    exposure = row.mode || row.exposure || null;
    statusLabel = row.status;
  }

  const rawUrlTrim = String(rawUrl ?? '').trim();
  const targetURL = normalizeTargetUrl(rawUrlTrim || name);
  const isDisabled =
    platform_category === MOBILE_APP || platform_category === IT_INFRASTRUCTURE;

  const plat = platformMapping[platform_category] || platform_category || '';
  const status = statusLabel ? String(statusLabel) : '';

  /** Match All Assets table: bold name, grey second line (URL or type • status) */
  const displayPrimary = name || rawUrl || 'Asset';
  const metaLine = [plat, status].filter(Boolean).join(' • ');
  let displaySecondary = '';
  if (name && rawUrl) {
    displaySecondary = rawUrl !== name ? rawUrl : metaLine || plat;
  } else if (rawUrl && !name) {
    displaySecondary = metaLine || '';
  } else if (name && !rawUrl) {
    displaySecondary = metaLine || '';
  }

  return {
    targetURL,
    assetId,
    assetDisplayName: name || rawUrl || 'Asset',
    displayPrimary,
    displaySecondary,
    platformCategory: platform_category === API ? API : WEB_APP,
    platform_category,
    assetExposure: exposure,
    isDisabled,
  };
}

/**
 * Step 1: search existing assets (same list API as All Assets v2) and pick one for scheduling.
 */
export default function ScheduleGenPTAssetStep({ onSelectAsset, companyId: companyIdProp }) {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(null);

  const companyId =
    companyIdProp ??
    (() => {
      try {
        return JSON.parse(Storage.decryptStorageData('company_id'))?.id;
      } catch {
        return null;
      }
    })();

  useEffect(() => {
    const t = setTimeout(() => {
      const q = inputValue.trim();
      setDebouncedSearch(q.length ? q : null);
    }, 350);
    return () => clearTimeout(t);
  }, [inputValue]);

  const hasActiveSearch = Boolean(debouncedSearch);

  const { data, isFetching, fetchNextPage, hasNextPage } = useAssetManagement({
    search: debouncedSearch,
    pageSize: 15,
    /** Only hit the API after the user searches — no full asset list on load */
    enabled: hasActiveSearch,
  });

  const assets = useMemo(() => {
    if (!hasActiveSearch) return [];
    return getAllDataFromPages(data?.pages, 'assets') || [];
  }, [data?.pages, hasActiveSearch]);

  const { ref: loadMoreRef, inView } = useInView({ threshold: 0 });

  useEffect(() => {
    if (!hasActiveSearch) return;
    if (inView && hasNextPage && !isFetching) {
      fetchNextPage?.();
    }
  }, [inView, hasNextPage, isFetching, fetchNextPage, hasActiveSearch]);

  const openAddNewAsset = () => {
    dispatch(
      setAddNewAssetsModal({
        open: true,
        company_id: companyId,
      })
    );
  };

  const inventoryHint = (
    <PermissionProvider roles={GENPT_SCHEDULE_CREATE_ASSET_ROLES}>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm text-gray-600">
        <span className="min-w-0 flex-1">
          Need to test a new asset? Add it to your inventory now.
        </span>
        <button
          type="button"
          className="flex-shrink-0 font-semibold text-primary-500 hover:underline whitespace-nowrap"
          onClick={openAddNewAsset}
        >
          + Create New Asset
        </button>
      </div>
    </PermissionProvider>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <img
          src={TARGET_ASSET_ICON_SRC}
          alt=""
          width={20}
          height={20}
          className="h-5 w-5 flex-shrink-0"
          aria-hidden
        />
        <Typography variant="subtitle1" className="!font-semibold !text-gray-800">
          Target Asset
        </Typography>
      </div>

      <MuiTextField
        fullWidth
        size="small"
        placeholder="Search by asset name or target URL.."
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        InputProps={{
          startAdornment: (
            <span className="inline-flex mr-2 text-gray-400">
              <SearchIcon />
            </span>
          ),
        }}
      />

      {!hasActiveSearch && inventoryHint}

      {hasActiveSearch && (
        <div className="max-h-[min(360px,50vh)] overflow-y-auto rounded-lg border border-gray-200 bg-white">
          {isFetching && assets.length === 0 && (
            <div className="flex min-h-[200px] items-center justify-center py-8">
              <CircularProgress size={40} thickness={4} sx={{ color: '#12AFB8' }} />
            </div>
          )}
          {assets.length === 0 && !isFetching && (
            <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
              <Typography variant="body1" className="!text-gray-600">
                No matching assets found in your inventory
              </Typography>
              <PermissionProvider roles={GENPT_SCHEDULE_CREATE_ASSET_ROLES}>
                <Button
                  type="button"
                  variant="outlined"
                  className="!mt-5 !px-4 !py-2 !text-xs !font-semibold !uppercase !tracking-wide !border-primary-500 !text-primary-500 hover:!bg-primary-50"
                  onClick={openAddNewAsset}
                >
                  + Create New Asset
                </Button>
              </PermissionProvider>
            </div>
          )}
          {assets.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {assets.map((row) => {
                const picked = mapRowToPickedAsset(row);
                const key = `${picked.assetId}-${picked.targetURL}`;
                const platformForIcon =
                  picked.platform_category ??
                  row?.asset?.platform_category ??
                  row?.platform_category;
                return (
                  <li key={key}>
                    <button
                      type="button"
                      disabled={picked.isDisabled}
                      onClick={() => !picked.isDisabled && onSelectAsset(picked)}
                      className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
                        picked.isDisabled
                          ? 'cursor-not-allowed opacity-50'
                          : 'hover:bg-primary-50 cursor-pointer'
                      }`}
                    >
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-secondary-50">
                        {showPlatformIcon(platformForIcon)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <Tooltip title={picked.displayPrimary} placement="top" disableHoverListener={!picked.displayPrimary}>
                          <Typography
                            variant="subtitle2"
                            className="!text-black max-w-full truncate !font-semibold"
                          >
                            {picked.displayPrimary}
                          </Typography>
                        </Tooltip>
                        {picked.displaySecondary ? (
                          <Tooltip title={picked.displaySecondary} placement="bottom" disableHoverListener={!picked.displaySecondary}>
                            <Typography
                              variant="body2"
                              className="!text-gray-500 max-w-full truncate !font-semibold"
                            >
                              {picked.displaySecondary}
                            </Typography>
                          </Tooltip>
                        ) : null}
                        {picked.isDisabled && (
                          <span className="mt-1 block text-xs text-amber-700">
                            GenPT scheduling is not available for this platform type.
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {assets.length > 0 && <div ref={loadMoreRef} className="h-2" aria-hidden />}
          {isFetching && assets.length > 0 && (
            <div className="flex justify-center py-3">
              <CircularProgress size={28} thickness={4} sx={{ color: '#12AFB8' }} />
            </div>
          )}
        </div>
      )}

      {hasActiveSearch && (isFetching || assets.length > 0) && inventoryHint}
    </div>
  );
}
